import { useState } from 'react';
import { CreateRidePayload } from '@/lib/api';
import { DestinationType } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { toast } from 'sonner';

interface FormDataType {
  destination: DestinationType;
  destinationName: string;
  pickupLocation: string;
  date: string;
  startTime: string;
  endTime: string;
  seatsAvailable: number;
  gasSplitCost: number;
}

interface PostRideFormProps {
  onSubmit: (data: CreateRidePayload) => Promise<void>;
  isSubmitting?: boolean;
}



export const PostRideForm = ({ onSubmit, isSubmitting = false }: PostRideFormProps) => {
  const [formData, setFormData] = useState<FormDataType>({
    destination: '' as DestinationType,
    destinationName: '',
    pickupLocation: '',
    date: '',
    startTime: '',
    endTime: '',
    seatsAvailable: 3,
    gasSplitCost: 5,
  });
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Validate all required fields
    if (!formData.destination.trim() || !formData.destinationName.trim() || !formData.pickupLocation.trim() || !formData.date || !formData.startTime || !formData.endTime || formData.seatsAvailable < 1 || formData.gasSplitCost < 1) {
      toast.error('Please fill all required fields.');
      return;
    }

    const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (!timePattern.test(formData.startTime) || !timePattern.test(formData.endTime)) {
      toast.error('Times must use 24-hour HH:MM format.');
      return;
    }

    const startDateTime = new Date(`${formData.date}T${formData.startTime}:00`);
    const endDateTime = new Date(`${formData.date}T${formData.endTime}:00`);
    if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
      toast.error('Please enter valid date and times.');
      return;
    }
    
    if (endDateTime <= startDateTime) {
      toast.error('End time must be after start time.');
      return;
    }
    
    const durationMinutes = Math.round((endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60));
    if (durationMinutes < 30) {
      toast.error('Ride duration must be at least 30 minutes.');
      return;
    }
    
    try {
      await onSubmit({ ...formData, destination: formData.destination });
      setIsSuccess(true);
      // Auto-reset success after 3s
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (error) {
      console.error('PostRideForm submit error:', error);
    }
  };

  return (
    <>
      {isSuccess && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl flex items-center gap-2 justify-center">
          <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
          <Badge className="pending text-sm">✅ PENDING - Check dashboard!</Badge>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Destination Type</label>
          <Select value={formData.destination} onValueChange={(value) => setFormData(prev => ({ ...prev, destination: value as DestinationType }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select destination type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="airport">Airport</SelectItem>
              <SelectItem value="grocery">Grocery</SelectItem>
              <SelectItem value="mall">Mall</SelectItem>
              <SelectItem value="campus">Campus</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Destination Name/Address</label>
          <AddressAutocomplete
            placeholder="e.g., Campus Gate"
            value={formData.destinationName}
            onChange={(destinationName) => setFormData(prev => ({ ...prev, destinationName }))}
            required
          />
        </div>


      <div className="space-y-2">
        <label className="text-sm font-medium">Pickup Location</label>
        <AddressAutocomplete
          placeholder="e.g., Student Center"
          value={formData.pickupLocation}
          onChange={(pickupLocation) => setFormData(prev => ({ ...prev, pickupLocation }))}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Date</label>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
            required
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Start Time (Pickup)</label>
          <Input
            type="time"
            value={formData.startTime}
            onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
            required
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">End Time (Arrival)</label>
          <Input
            type="time"
            value={formData.endTime}
            onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Available Seats</label>
        <div className="flex items-center gap-2">
          <Button 
            type="button"
            variant="outline"
            size="sm"
            disabled={isSubmitting}
            onClick={() => setFormData(prev => ({ ...prev, seatsAvailable: Math.max(1, prev.seatsAvailable - 1) }))}
            className="h-10 w-10 p-0 rounded-lg"
          >
            -
          </Button>
          <span className="w-16 text-center font-mono font-bold text-lg px-4 py-2 bg-muted rounded-lg">
            {formData.seatsAvailable}
          </span>
          <Button 
            type="button"
            variant="outline"
            size="sm"
            disabled={isSubmitting}
            onClick={() => setFormData(prev => ({ ...prev, seatsAvailable: Math.min(8, prev.seatsAvailable + 1) }))}
            className="h-10 w-10 p-0 rounded-lg"
          >
            +
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">$ Gas Split Cost (per person)</label>
        <div className="flex items-center gap-2">
          <Button 
            type="button"
            variant="outline"
            size="sm"
            disabled={isSubmitting}
            onClick={() => setFormData(prev => ({ ...prev, gasSplitCost: Math.max(1, prev.gasSplitCost - 0.5) }))}
            className="h-10 w-10 p-0 rounded-lg"
          >
            -
          </Button>
          <span className="w-20 text-center font-mono font-bold text-lg px-4 py-2 bg-muted rounded-lg">
            ${formData.gasSplitCost.toFixed(1)}
          </span>
          <Button 
            type="button"
            variant="outline"
            size="sm"
            disabled={isSubmitting}
            onClick={() => setFormData(prev => ({ ...prev, gasSplitCost: Math.min(50, prev.gasSplitCost + 0.5) }))}
            className="h-10 w-10 p-0 rounded-lg"
          >
            +
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">Minimum $1.00, tap +/- to adjust</p>
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full h-12 rounded-xl gradient-warm"
      >
        {isSubmitting ? 'Posting...' : 'Post Ride'}
      </Button>
    </form>
    </>
  );
};

export default PostRideForm;
